// services/microsoft-graph.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PublicClientApplication, InteractionRequiredAuthError, AccountInfo } from '@azure/msal-browser';

@Injectable({
  providedIn: 'root'
})
export class MicrosoftGraphService {
  private graphApiUrl = 'https://graph.microsoft.com/v1.0';
  private msal: PublicClientApplication | null = null;
  private accessToken: string | null = null;
  private account: AccountInfo | null = null;

  /**
   * The well-known tenant every personal Microsoft account (outlook.com, hotmail.com, live.com)
   * reports. Work/school accounts carry their own tenant guid. Several code paths need to tell the
   * two apart — MSA needs the /consumers authority rather than a tenant-specific one, and a number
   * of Graph resources (mailboxSettings, the directory behind user.readbasic.all) simply do not
   * exist for it.
   */
  private static readonly MSA_TENANT_ID = '9188040d-6c67-4c5b-b112-36a304b66dad';

  // MSAL Configuration - Using your actual client ID
  private msalConfig = {
    auth: {
      clientId: 'c5378750-03d4-48b4-8f45-24bb479636f6', // Your actual client ID
      authority: 'https://login.microsoftonline.com/common', // Using 'common' for multi-tenant
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: false // Prevent MSAL from automatically navigating
    },
    cache: {
      // sessionStorage isolates this PCA from the two B2C instances that use
      // localStorage. Sharing localStorage caused msal.interaction.status,
      // msal.account.keys, and msal.token.keys to collide across PCAs,
      // producing interaction_in_progress and AADB2C90238 (B2C consuming an
      // AAD-issued redirect hash). Silent SSO via login.microsoftonline.com
      // cookies keeps re-login invisible after browser restart.
      cacheLocation: 'sessionStorage' as const,
      storeAuthStateInCookie: false
    },
    system: {
      allowNativeBroker: false, // Disable native broker to avoid conflicts
      loggerOptions: {
        loggerCallback: (level: any, message: string, containsPii: boolean) => {
          if (!containsPii) {
          }
        },
        piiLoggingEnabled: false,
        logLevel: 3 // Info level
      }
    }
  };

  // Scopes required for Microsoft Graph API
  private scopes = [
    'user.read',
    'mail.read',
   // 'mail.readwrite',
    //'mail.send',
    'mailboxsettings.read',
    // The phonebook reads the signed-in user's Outlook contacts and directory photos. Both are
    // read-only and user-consentable; 'user.readbasic.all' is what allows a colleague's photo to
    // be fetched by their address rather than only the current user's own.
    'contacts.read',
    'user.readbasic.all'
  ];

  /**
   * Calendar is requested on its own, only when the Outlook panel's Calendar tab asks for it —
   * never as part of the base set above.
   *
   * Widening `scopes` would be the obvious move and it is the wrong one. MSAL matches cached tokens
   * against the *requested* scope set, so every existing silent acquisition — the phonebook's
   * contact load included — would suddenly be asking for a scope the user has never consented to.
   * That turns a working silent call into an unprompted popup on page load, or an outright
   * AADSTS65001 in tenants where user consent is disabled. Keeping the extra scope on a separate
   * list means an ungranted calendar permission degrades exactly one tab and nothing else.
   */
  private calendarScopes = [...this.scopes, 'calendars.read'];

  private initPromise: Promise<void> | null = null;

  constructor(private http: HttpClient) {
    // Deliberately empty of MSAL work. The phonebook sidebar lives in the app shell and injects
    // this service, so the constructor runs on every page load — including the one that lands a
    // B2C sign-in response. Anything MSAL does here races that sign-in; see the getter below.
  }

  /**
   * The M365 instance, built on first use.
   *
   * THIS INSTANCE MUST NEVER TOUCH A REDIRECT RESPONSE. It is the third PublicClientApplication in
   * the tab, alongside the two B2C ones, and msal-browser gives every instance the same view of two
   * shared things:
   *
   *  - window.location.hash. handleRedirectPromise() recognises *any* redirect response there,
   *    whoever asked for it, and calls clearHash() before checking its own request cache. Calling
   *    it here consumed the B2C sign-in response, so the B2C instance found nothing, MsalGuard saw
   *    no account, and the user was bounced to /login-failed — from where the app shell's own API
   *    calls kicked off a fresh loginRedirect(), i.e. an endless reload.
   *  - msal.interaction.status: one sessionStorage slot, not prefixed by client id. While it names
   *    this client, the B2C instances refuse to process their own redirect and loginRedirect()
   *    throws interaction_in_progress.
   *
   * Hence: no handleRedirectPromise anywhere in this service, and no redirect-based flows either —
   * a redirect started here could only be completed by the very call that breaks sign-in. Every
   * interactive step is a popup, which keeps its response inside the popup window and never touches
   * the hosting page's URL. RedirectHandlerService applies the same rule to the customer PCA.
   */
  private get msalInstance(): PublicClientApplication {
    if (!this.msal) {
      this.msal = new PublicClientApplication(this.msalConfig);
      this.releaseStaleInteractionLock();
    }
    return this.msal;
  }

  private async initializeAuth(): Promise<void> {
    try {
      // Initialize MSAL instance
      await this.msalInstance.initialize();

      // Adopt an account already in the cache. Note the absence of handleRedirectPromise() — see
      // the msalInstance getter for why it must never be called here.
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        // Use the first account, but verify it works with current authority
        this.account = accounts[0];
        this.msalInstance.setActiveAccount(accounts[0]);

        // Try to acquire a token silently to verify the account works
        try {
          await this.msalInstance.acquireTokenSilent({
            scopes: ['user.read'],
            account: accounts[0]
          });
        } catch (silentError) {
          // If silent acquisition fails, do not clear cache automatically
          console.warn('Silent token acquisition failed during init:', silentError);
          // Keep the account and let future acquireToken handle interaction if needed
        }
      }
    } catch (error) {
      console.error('MSAL initialization error:', error);
    }
  }

  /**
   * Clears an interaction lock left behind by this client — and only this client.
   *
   * A popup cannot outlive a page load, so if the shared msal.interaction.status still names us at
   * the moment we are first built, it is a leftover from a popup the user closed by reloading. Left
   * in place it blocks the *B2C* sign-in for the rest of the tab session. The two B2C clients' own
   * locks are never touched.
   */
  private releaseStaleInteractionLock(): void {
    try {
      const key = 'msal.interaction.status';
      if (sessionStorage.getItem(key) === this.msalConfig.auth.clientId) {
        sessionStorage.removeItem(key);
      }
    } catch {
      // sessionStorage blocked (private mode) — nothing to clean up in that case anyway.
    }
  }

  // Helper method to ensure initialization. The first call builds and initializes the instance, so
  // a user who never opens Outlook or the phonebook causes no MSAL activity at all.
  private async ensureInitialized(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.initializeAuth();
    }
    await this.initPromise;
  }

  // Authentication Methods
  async login(): Promise<void> {
    try {
      // Ensure MSAL is initialized
      await this.ensureInitialized();

      const loginResponse = await this.msalInstance.loginPopup({
        scopes: this.scopes,
        prompt: 'select_account',
        authority: this.msalConfig.auth.authority
      });
      
      this.account = loginResponse.account;
      this.msalInstance.setActiveAccount(loginResponse.account);
      
      // Get access token
      await this.acquireToken();
    } catch (error) {
      // No redirect fallback: a redirect started here could only be completed by
      // handleRedirectPromise(), which is exactly what breaks the BizCombo sign-in. A blocked popup
      // is reported as such, so the caller can ask the user to allow popups instead of navigating
      // the whole app away to Microsoft.
      if (error instanceof Error && error.message.includes('popup')) {
        throw new Error('Microsoft sign-in needs a popup. Allow popups for this site, then connect Outlook again.');
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.ensureInitialized();
      
      if (this.account) {
        await this.msalInstance.logoutPopup({
          account: this.account,
          postLogoutRedirectUri: this.msalConfig.auth.postLogoutRedirectUri
        });
        this.account = null;
        this.accessToken = null;
      }
    } catch (error) {
      // No logoutRedirect fallback either: it sets the shared interaction lock and then navigates
      // the whole page to Microsoft, and nothing on the way back clears that lock for this client —
      // which leaves the BizCombo sign-in jammed. Dropping the local cache disconnects Outlook from
      // this app, which is what the user asked for; the Microsoft session itself just remains.
      console.warn('Microsoft logout popup failed, clearing the local M365 session instead:', error);
      await this.clearCache();
    }
  }

  /**
   * @param scopes defaults to the base set, so every pre-existing caller keeps its exact behaviour.
   *               Pass `calendarScopes` to reach a calendar-scoped token without widening what the
   *               rest of the app silently asks for.
   */
  private async acquireToken(scopes: string[] = this.scopes): Promise<string> {
    await this.ensureInitialized();
    
    if (!this.account) {
      // Try to get account from MSAL
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.account = accounts[0];
        this.msalInstance.setActiveAccount(accounts[0]);
      } else {
        throw new Error('No active account. Please login first.');
      }
    }
  
    // Helper to get account-specific authority
    const getAccountAuthority = (account: AccountInfo): string => {
      if (account.tenantId === MicrosoftGraphService.MSA_TENANT_ID) {
        // Personal Microsoft Account (MSA)
        return 'https://login.microsoftonline.com/consumers';
      }
      // Azure AD account
      return `https://login.microsoftonline.com/${account.tenantId}`;
    };
  
    try {
      // Try to acquire token silently with default config
      const response = await this.msalInstance.acquireTokenSilent({
        scopes,
        account: this.account
      });

      this.accessToken = response.accessToken;
      return response.accessToken;
    } catch (error) {
      // Check if it's the authority mismatch error
      if (error instanceof Error && error.message.includes('authority_mismatch')) {
        console.warn('Authority mismatch detected. Retrying with account-specific authority.');
        
        const accountAuthority = getAccountAuthority(this.account);
        
        try {
          // Retry silent with overridden authority
          const response = await this.msalInstance.acquireTokenSilent({
            scopes,
            account: this.account,
            authority: accountAuthority
          });
          
          this.accessToken = response.accessToken;
          return response.accessToken;
        } catch (retryError) {
          // If retry silent fails, fall back to interactive popup with overridden authority
          if (retryError instanceof InteractionRequiredAuthError) {
            try {
              const response = await this.msalInstance.acquireTokenPopup({
                scopes,
                authority: accountAuthority
              });
              this.accessToken = response.accessToken;
              return response.accessToken;
            } catch (popupError) {
              if (popupError instanceof Error && popupError.message.includes('authority_mismatch')) {
                await this.clearCache();
                throw new Error('Authority mismatch in interactive login. Cache cleared. Please try logging in again.');
              }
              throw popupError;
            }
          }
          // If still fails, clear cache and throw
          await this.clearCache();
          throw new Error('Authority mismatch could not be resolved. Cache cleared. Please try logging in again.');
        }
      }
      
      // For other InteractionRequired errors, fall back to popup with default
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const response = await this.msalInstance.acquireTokenPopup({
            scopes
          });
          this.accessToken = response.accessToken;
          return response.accessToken;
        } catch (popupError) {
          // No acquireTokenRedirect fallback — see the msalInstance getter. Surfacing the failure
          // costs the caller one Graph feature; navigating away would cost the user their session.
          throw popupError;
        }
      }
      throw error;
    }
  }

  private async getHeaders(scopes?: string[]): Promise<HttpHeaders> {
    const token = await this.acquireToken(scopes);
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  // User Profile Methods
  async getUserProfile(): Promise<any> {
    try {
      await this.ensureInitialized();
      
      // First check if we have an account
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        throw new Error('No authenticated user');
      }
      
      const headers = await this.getHeaders();
      return await this.http.get<any>(
        `${this.graphApiUrl}/me`,
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async getUserPhoto(): Promise<Blob> {
    try {
      const headers = await this.getHeaders();
      return await this.http.get(
        `${this.graphApiUrl}/me/photo/$value`,
        { headers, responseType: 'blob' }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  // ── Contacts & photos (used by the phonebook) ──

  /**
   * The signed-in user's Outlook contacts.
   *
   * Personal to that mailbox — this is not the tenant directory. It exists so the numbers someone
   * has already collected in Outlook are searchable next to the ones in BC, rather than living in
   * a separate app. A tenant-wide sync is a server job; see
   * `backend-todo-outlook-contact-sync.md`.
   */
  async getOutlookContacts(top = 500): Promise<any[]> {
    const headers = await this.getHeaders();
    const params = new HttpParams()
      .set('$top', String(top))
      .set('$select', 'id,displayName,givenName,surname,emailAddresses,mobilePhone,businessPhones,companyName,jobTitle,department');

    const res = await this.http.get<any>(`${this.graphApiUrl}/me/contacts`, { headers, params }).toPromise();
    return res?.value ?? [];
  }

  /**
   * A directory user's photo, by object id or user principal name (their email works).
   *
   * Graph answers 404 when someone simply has no photo set, which is not an error worth surfacing —
   * the caller falls back to initials.
   */
  async getPhotoFor(userIdOrUpn: string): Promise<Blob | null> {
    try {
      const headers = await this.getHeaders();
      return await this.http.get(
        `${this.graphApiUrl}/users/${encodeURIComponent(userIdOrUpn)}/photo/$value`,
        { headers, responseType: 'blob' }
      ).toPromise();
    } catch {
      return null;
    }
  }

  /** Photo attached to one of the user's own Outlook contacts. */
  async getOutlookContactPhoto(contactId: string): Promise<Blob | null> {
    try {
      const headers = await this.getHeaders();
      return await this.http.get(
        `${this.graphApiUrl}/me/contacts/${encodeURIComponent(contactId)}/photo/$value`,
        { headers, responseType: 'blob' }
      ).toPromise();
    } catch {
      return null;
    }
  }

  /** True when Graph can be called without prompting — the phonebook degrades quietly otherwise. */
  isConnected(): boolean {
    return this.msalInstance.getAllAccounts().length > 0;
  }

  // Email Methods
  async getMails(options: {
    folder?: string;
    top?: number;
    orderby?: string;
    select?: string;
  } = { folder: 'inbox', top: 50, orderby: 'receivedDateTime desc', select: 'id,subject,bodyPreview,body,from,sender,toRecipients,ccRecipients,bccRecipients,receivedDateTime,hasAttachments,isRead,flag,importance' }): Promise<any> {
    try {
      const headers = await this.getHeaders();
      let params = new HttpParams();
  
      if (options.top) params = params.set('$top', options.top.toString());
      if (options.orderby) params = params.set('$orderby', options.orderby);
      if (options.select) params = params.set('$select', options.select);
  
      const folderPath = options.folder === 'inbox' ? 'mailFolders/inbox/messages' : `mailFolders/${options.folder}/messages`;
  
      return await this.http.get<any>(
        `${this.graphApiUrl}/me/${folderPath}`,
        { headers, params }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async getMail(messageId: string, options?: { select?: string }): Promise<any> {
    try {
      const headers = await this.getHeaders();
      let params = new HttpParams();
      
      if (options?.select) {
        params = params.set('$select', options.select);
      }
      
      // Expand attachments to get them with the email
      params = params.set('$expand', 'attachments');
      
      return await this.http.get<any>(
        `${this.graphApiUrl}/me/messages/${messageId}`,
        { headers, params }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async sendMail(messageId: string | null, mailData?: any): Promise<void> {
    try {
      const headers = await this.getHeaders();
      const url = messageId ? `${this.graphApiUrl}/me/messages/${messageId}/send` : `${this.graphApiUrl}/me/sendMail`;
      
      await this.http.post(
        url,
        messageId ? null : (mailData ?? null),
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }
  async createDraft(draftData: any): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      return await this.http.post<any>(
        `${this.graphApiUrl}/me/messages`,
        draftData,
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }
  async updateMail(messageId: string, updates: any): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      return await this.http.patch<any>(
        `${this.graphApiUrl}/me/messages/${messageId}`,
        updates,
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async deleteMail(messageId: string): Promise<void> {
    try {
      const headers = await this.getHeaders();
      
      await this.http.delete(
        `${this.graphApiUrl}/me/messages/${messageId}`,
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async markAsRead(messageId: string, isRead: boolean = true): Promise<any> {
    try {
      return await this.updateMail(messageId, { isRead });
    } catch (error) {
      throw error;
    }
  }

  async moveMail(messageId: string, destinationFolderId: string): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      return await this.http.post<any>(
        `${this.graphApiUrl}/me/messages/${messageId}/move`,
        { destinationId: destinationFolderId },
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async copyMail(messageId: string, destinationFolderId: string): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      return await this.http.post<any>(
        `${this.graphApiUrl}/me/messages/${messageId}/copy`,
        { destinationId: destinationFolderId },
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  // Reply Methods
  async replyToMail(messageId: string, comment: string): Promise<void> {
    try {
      const headers = await this.getHeaders();
      
      const replyData = {
        comment: comment
      };

      await this.http.post(
        `${this.graphApiUrl}/me/messages/${messageId}/reply`,
        replyData,
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async replyAllToMail(messageId: string, comment: string): Promise<void> {
    try {
      const headers = await this.getHeaders();
      
      const replyData = {
        comment: comment
      };

      await this.http.post(
        `${this.graphApiUrl}/me/messages/${messageId}/replyAll`,
        replyData,
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async forwardMail(messageId: string, comment: string, toRecipients: string[]): Promise<void> {
    try {
      const headers = await this.getHeaders();
      
      const forwardData = {
        comment: comment,
        toRecipients: toRecipients.map(email => ({
          emailAddress: { address: email }
        }))
      };

      await this.http.post(
        `${this.graphApiUrl}/me/messages/${messageId}/forward`,
        forwardData,
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  // Folder Methods
  async getMailFolders(): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      return await this.http.get<any>(
        `${this.graphApiUrl}/me/mailfolders`,
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async createMailFolder(displayName: string, parentFolderId?: string): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      const url = parentFolderId 
        ? `${this.graphApiUrl}/me/mailfolders/${parentFolderId}/childFolders`
        : `${this.graphApiUrl}/me/mailfolders`;
      
      return await this.http.post<any>(
        url,
        { displayName },
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async deleteMailFolder(folderId: string): Promise<void> {
    try {
      const headers = await this.getHeaders();
      
      await this.http.delete(
        `${this.graphApiUrl}/me/mailfolders/${folderId}`,
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  // Attachment Methods
  async getAttachments(messageId: string): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      return await this.http.get<any>(
        `${this.graphApiUrl}/me/messages/${messageId}/attachments`,
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async getAttachment(messageId: string, attachmentId: string): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      return await this.http.get<any>(
        `${this.graphApiUrl}/me/messages/${messageId}/attachments/${attachmentId}`,
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async downloadAttachment(messageId: string, attachmentId: string, fileName: string): Promise<void> {
    try {
      const attachment = await this.getAttachment(messageId, attachmentId);
      
      if (attachment.contentBytes || attachment['@odata.mediaContentType']) {
        let blob: Blob;
        
        if (attachment.contentBytes) {
          // Convert base64 to blob
          const byteCharacters = atob(attachment.contentBytes);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          blob = new Blob([byteArray], { type: attachment.contentType });
        } else {
          // For large attachments, fetch the content separately
          const headers = await this.getHeaders();
          const response = await this.http.get(
            `${this.graphApiUrl}/me/messages/${messageId}/attachments/${attachmentId}/$value`,
            { headers, responseType: 'blob' }
          ).toPromise();
          blob = response;
        }
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || attachment.name || 'attachment';
        link.click();
        
        // Cleanup
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Attachment content not available');
      }
    } catch (error) {
      throw error;
    }
  }

  async addAttachment(messageId: string, attachment: {
    name: string;
    contentType: string;
    contentBytes: string;
  }): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      const attachmentData = {
        '@odata.type': '#microsoft.graph.fileAttachment',
        name: attachment.name,
        contentType: attachment.contentType,
        contentBytes: attachment.contentBytes
      };

      return await this.http.post<any>(
        `${this.graphApiUrl}/me/messages/${messageId}/attachments`,
        attachmentData,
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  async deleteAttachment(messageId: string, attachmentId: string): Promise<void> {
    try {
      const headers = await this.getHeaders();
      
      await this.http.delete(
        `${this.graphApiUrl}/me/messages/${messageId}/attachments/${attachmentId}`,
        { headers }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  // Search Methods
  async searchMails(searchQuery: string, options?: {
    top?: number;
    select?: string;
  }): Promise<any> {
    try {
      const headers = await this.getHeaders();
      let params = new HttpParams();
      
      params = params.set('$search', `"${searchQuery}"`);
      if (options?.top) params = params.set('$top', options.top.toString());
      if (options?.select) params = params.set('$select', options.select);

      return await this.http.get<any>(
        `${this.graphApiUrl}/me/messages`,
        { headers, params }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  // Calendar Methods (bonus)
  async getCalendarEvents(options?: {
    startDateTime?: string;
    endDateTime?: string;
    top?: number;
  }): Promise<any> {
    try {
      const headers = await this.getHeaders();
      let params = new HttpParams();
      
      if (options?.top) params = params.set('$top', options.top.toString());
      if (options?.startDateTime && options?.endDateTime) {
        params = params.set('$filter', 
          `start/dateTime ge '${options.startDateTime}' and end/dateTime le '${options.endDateTime}'`
        );
      }

      return await this.http.get<any>(
        `${this.graphApiUrl}/me/calendar/events`,
        { headers, params }
      ).toPromise();
    } catch (error) {
      throw error;
    }
  }

  // Utility Methods
  async isAuthenticated(): Promise<boolean> {
    await this.ensureInitialized();
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0;
  }

  async getActiveAccount(): Promise<AccountInfo | null> {
    await this.ensureInitialized();
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      this.account = accounts[0];
      return accounts[0];
    }
    return null;
  }

  async refreshToken(): Promise<string> {
    return await this.acquireToken();
  }

  // Clear cache method for troubleshooting
  async clearCache(): Promise<void> {
    await this.ensureInitialized();
    
    // Clear MSAL cache
    try {
      await this.msalInstance.clearCache();
    } catch (error) {
    }
    
    this.account = null;
    this.accessToken = null;
    
    // Clear localStorage items related to MSAL
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('msal') || key.includes('login.windows') || key.includes('login.microsoftonline'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  // Outlook panel
  //
  // Read-only additions backing the global Outlook panel. Everything here uses scopes the base
  // sign-in already consents, with the single exception of the calendar pair, which carries its own
  // scope list and its own consent handshake.
  // ═══════════════════════════════════════════════════════════════════════════════════════════════

  /**
   * The signed-in user's profile, projected onto the fields a signature block needs.
   *
   * `getUserProfile()` above cannot serve this: it sends no $select, and Graph's default /me
   * projection omits department, companyName, city, country, streetAddress, postalCode and
   * faxNumber — precisely the fields that distinguish a signature from a name and an email address.
   */
  async getUserProfileForSignature(): Promise<any> {
    const headers = await this.getHeaders();
    const params = new HttpParams().set(
      '$select',
      'displayName,givenName,surname,jobTitle,department,companyName,mail,userPrincipalName,' +
        'mobilePhone,businessPhones,officeLocation,city,country,streetAddress,postalCode,faxNumber'
    );

    return await this.http.get<any>(`${this.graphApiUrl}/me`, { headers, params }).toPromise();
  }

  /**
   * Mailbox settings: time zone, language, working hours and automatic replies.
   *
   * Returns null rather than throwing on personal Microsoft accounts, where the resource does not
   * exist and Graph answers 400 or 501. That is a shape-of-account fact, not a failure, and the
   * Profile tab renders an explanatory note for it.
   */
  async getMailboxSettings(): Promise<any | null> {
    try {
      const headers = await this.getHeaders();
      return await this.http.get<any>(`${this.graphApiUrl}/me/mailboxSettings`, { headers }).toPromise();
    } catch {
      return null;
    }
  }

  /** Item and unread counts for a mail folder. Cheap enough to drive the folder pills. */
  async getMailFolderStats(folder = 'inbox'): Promise<any | null> {
    try {
      const headers = await this.getHeaders();
      const params = new HttpParams().set('$select', 'displayName,totalItemCount,unreadItemCount');
      return await this.http
        .get<any>(`${this.graphApiUrl}/me/mailFolders/${encodeURIComponent(folder)}`, { headers, params })
        .toPromise();
    } catch {
      return null;
    }
  }

  /** Most recent sent message, used as the source for signature detection. Null when unavailable. */
  async getLastSentMessage(): Promise<any | null> {
    try {
      const headers = await this.getHeaders();
      const params = new HttpParams()
        .set('$top', '1')
        .set('$orderby', 'sentDateTime desc')
        .set('$select', 'id,subject,sentDateTime,body');

      const res = await this.http
        .get<any>(`${this.graphApiUrl}/me/mailFolders/sentitems/messages`, { headers, params })
        .toPromise();
      return res?.value?.[0] ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Calendar occurrences in a window.
   *
   * Uses /me/calendarView, not /me/calendar/events. The distinction matters: `events` returns the
   * series master for anything recurring, so a weekly stand-up would appear once with its original
   * start date. `calendarView` expands a series into the individual occurrences an agenda is
   * actually made of.
   *
   * The Prefer header asks Graph to return start/end already converted to the mailbox time zone,
   * which saves the client guessing and getting DST wrong.
   */
  async getCalendarView(
    startIso: string,
    endIso: string,
    top = 100,
    timeZone?: string | null
  ): Promise<any[]> {
    let headers = await this.getHeaders(this.calendarScopes);
    if (timeZone) {
      headers = headers.set('Prefer', `outlook.timezone="${timeZone}"`);
    }

    const params = new HttpParams()
      .set('startDateTime', startIso)
      .set('endDateTime', endIso)
      .set('$orderby', 'start/dateTime')
      .set('$top', String(top))
      .set(
        '$select',
        'id,subject,start,end,isAllDay,location,organizer,attendees,onlineMeeting,onlineMeetingUrl,webLink,showAs,isCancelled'
      );

    const res = await this.http.get<any>(`${this.graphApiUrl}/me/calendarView`, { headers, params }).toPromise();
    return res?.value ?? [];
  }

  /**
   * Whether a calendar-scoped token can be had without prompting.
   *
   * Strictly silent. Opening the Calendar tab runs this, and a tab that opens a popup merely by
   * being looked at would be indefensible — the prompt has to follow a click on the button that
   * says it will prompt.
   */
  async hasCalendarConsent(): Promise<boolean> {
    try {
      await this.ensureInitialized();
      const account = this.account ?? this.msalInstance.getAllAccounts()[0];
      if (!account) {
        return false;
      }
      await this.msalInstance.acquireTokenSilent({ scopes: this.calendarScopes, account });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Asks for calendar consent interactively. Call only from a click handler.
   *
   * Mirrors `login()`'s popup-then-redirect shape, so a blocked popup degrades the same way here as
   * it does at sign-in rather than dead-ending.
   */
  async requestCalendarConsent(): Promise<void> {
    await this.ensureInitialized();
    try {
      const response = await this.msalInstance.acquireTokenPopup({ scopes: this.calendarScopes });
      if (response.account) {
        this.account = response.account;
        this.msalInstance.setActiveAccount(response.account);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message.includes('popup')) {
        throw new Error('Calendar access needs a popup. Allow popups for this site and try again.');
      }
      throw error;
    }
  }

  /**
   * True for personal Microsoft accounts (outlook.com and friends).
   *
   * Several things are simply absent for these — mailboxSettings, and the directory that
   * user.readbasic.all reads — so callers use this to skip requests that can only fail rather than
   * showing the user an error for something that was never going to work.
   */
  isPersonalAccount(): boolean {
    const account = this.account ?? this.msalInstance.getAllAccounts()[0] ?? null;
    return account?.tenantId === MicrosoftGraphService.MSA_TENANT_ID;
  }

  /** Who is signed in, for the panel's account chip and for diagnosing wrong-account support calls. */
  getAccountSummary(): { username: string; name: string; tenantId: string; isPersonal: boolean } | null {
    const account = this.account ?? this.msalInstance.getAllAccounts()[0] ?? null;
    if (!account) {
      return null;
    }
    return {
      username: account.username ?? '',
      name: account.name ?? account.username ?? '',
      tenantId: account.tenantId ?? '',
      isPersonal: account.tenantId === MicrosoftGraphService.MSA_TENANT_ID
    };
  }

  /**
   * Attempts a silent, promptless sign-in using an existing Microsoft session cookie.
   *
   * The cache is sessionStorage (see the note on msalConfig), so closing every tab drops the
   * account and the panel goes back to "not connected" even though the login.microsoftonline.com
   * cookie usually outlives it. This turns that case back into a connected panel with no visible
   * interaction. Every failure is swallowed: when it does not work the user simply sees the normal
   * Connect button, which is exactly where they would have been anyway.
   */
  async trySilentSignIn(loginHint: string): Promise<boolean> {
    try {
      await this.ensureInitialized();
      if (this.msalInstance.getAllAccounts().length > 0) {
        return true;
      }
      const response = await this.msalInstance.ssoSilent({ scopes: this.scopes, loginHint });
      if (response.account) {
        this.account = response.account;
        this.msalInstance.setActiveAccount(response.account);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  // Helper method to convert file to base64
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        // Remove data URL prefix (e.g., "data:image/png;base64,")
        const base64Content = base64.split(',')[1];
        resolve(base64Content);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Helper method to get MIME type
  getMimeType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    const mimeTypes: { [key: string]: string } = {
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'txt': 'text/plain',
      'zip': 'application/zip',
      'rar': 'application/x-rar-compressed'
    };
    
    return mimeTypes[extension || ''] || 'application/octet-stream';
  }
}